import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { WebsocketManager } from '../../../../utils/websocket/websocket-manager';
import { SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useSubscribeCallback } from '../../../hooks/use-subscribe';
import { Button } from '../../button/button';
import { CheckBox } from '../../check-box/check-box';
import { InfoBox } from '../../info-box/info-box';
import { useGetGeodataImageListQuery } from '../geodata-image-list/use-get-geodata-image-list-query';
import { UploadStepStatus } from './use-upload-boundary-polygons-step';
import { UseImageUploadQueueStateResult } from './use-upload-images-step';

const TIMEOUT_MS = 8 * 60 * 1000; // 8 minutes

type ControlStatus = 'idle' | 'loading' | 'success';

type UseControlImagesResult = {
  status: ControlStatus;
  enabledCount: number;
  totalCount: number;
};

function useListenToGeodataImageFailedCount(geodataId: string) {
  const [failedImages, setFailedImages] = useState<string[]>([]);
  const completedImagesSetRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    void WebsocketManager.instance.waitForWebsocketConnection();
  }, []);

  useSubscribeCallback(SubscriptionTopic.WebsocketMessageRecieved, (data) => {
    if (data.message === 'GeodataImageUpdated' && data.geodataId === geodataId) {
      if (data.status === 'COMPLETED') {
        // Track completed images so a late FAILED message (e.g. from a duplicate lambda
        // invocation hitting a DB unique constraint) cannot override a prior COMPLETED.
        completedImagesSetRef.current.add(data.imageName);

        // Also remove from failed list in case FAILED arrived first.
        setFailedImages((prev) => {
          const idx = prev.indexOf(data.imageName);
          if (idx === -1) {
            return prev;
          }
          const next = [...prev];
          next.splice(idx, 1);
          return next;
        });
      } else if (data.status === 'FAILED') {
        // Ignore if this image already completed — the FAILED is from a duplicate invocation.
        if (completedImagesSetRef.current.has(data.imageName)) {
          return;
        }

        setFailedImages((prev) => {
          if (prev.includes(data.imageName)) {
            return prev;
          }
          return [...prev, data.imageName];
        });
      }
    }
  });

  return { failedImages };
}

/**
 * Controls images after upload by checking if all uploaded images are enabled.
 * Polls indefinitely until all images are verified or the user continues anyway.
 * After TIMEOUT_MS, shows a timeout UI so the user can continue with partial results.
 * Images that received websocket FAILED messages are shown separately from unverified ones.
 */
export function useControlImagesStep(
  geodataId: string | undefined,
  imageUploadQueue: UseImageUploadQueueStateResult,
  imageStepStatus: UploadStepStatus,
): UseControlImagesResult & {
  uploadStepStatus: UploadStepStatus;
  errorList: React.ReactNode;
} {
  const { failedImages } = useListenToGeodataImageFailedCount(geodataId || '');
  const [status, setStatus] = useState<ControlStatus>(
    imageUploadQueue.totalCount === 0 ? 'success' : 'idle',
  );
  const [enabledCount, setEnabledCount] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [unverifiedImages, setUnverifiedImages] = useState<string[]>([]);
  const hasStartedRef = useRef(false);
  const imagesRef = useRef<typeof images>([]);
  const pollingCancelledRef = useRef(false);

  // Query is disabled when geodataId is undefined (handled by query hook)
  const { images, refetchImages } = useGetGeodataImageListQuery(
    geodataId || '',
    imageUploadQueue.totalCount,
  );

  const uploadedImageCount = imageUploadQueue.completedCount;

  const continueAnyway = useCallback(() => {
    pollingCancelledRef.current = true;
    setStatus('success');
  }, []);

  // Recompute unverified list whenever poll results or websocket failures change.
  // Unverified = uploaded but not yet enabled/aligned AND not explicitly failed via websocket.
  useEffect(() => {
    if (status !== 'loading') {
      return;
    }
    // Server names are prefixed (e.g. "1716472378_DJI_…"); strip the prefix so
    // both sets can be compared against the user-uploaded filenames in imageUploadQueue.files.
    const stripPrefix = (n: string) => n.substring(n.indexOf('_') + 1);
    const enabledNames = new Set(
      imagesRef.current
        .filter((img) => img.enabled && img.aligned === true)
        .map((img) => stripPrefix(img.name)),
    );
    const failedNames = new Set(failedImages.map(stripPrefix));
    setUnverifiedImages(
      imageUploadQueue.files.filter(
        (fileName) => !enabledNames.has(fileName) && !failedNames.has(fileName),
      ),
    );
  }, [enabledCount, failedImages, imageUploadQueue.files, status]);

  const errorList = useMemo<React.ReactNode>(
    () => (
      <ControlImagesErrorBox
        checkboxChecked={checkboxChecked}
        enabledCount={enabledCount}
        failedImages={failedImages}
        status={status}
        timedOut={timedOut}
        unverifiedImages={unverifiedImages}
        onCheckboxChange={setCheckboxChecked}
        onContinueAnyway={continueAnyway}
      />
    ),
    [
      timedOut,
      status,
      enabledCount,
      failedImages,
      checkboxChecked,
      continueAnyway,
      unverifiedImages,
    ],
  );

  // Map hook status to StepStatus
  const uploadStepStatus = useMemo<UploadStepStatus>(() => {
    if (status === 'idle') {
      return 'pending';
    }
    if (status === 'loading') {
      return 'loading';
    }
    return 'success';
  }, [status]);

  // Keep ref in sync with latest images
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  // Start polling effect - only when we're in the controlImages phase
  // Polling should only start when:
  // - geodataId is defined
  // - image upload step succeeded (imageStepStatus === 'success')
  // - there are uploaded images to control
  useEffect(() => {
    if (
      !geodataId ||
      imageStepStatus !== 'success' ||
      uploadedImageCount === 0 ||
      status !== 'idle' ||
      hasStartedRef.current
    ) {
      return;
    }

    hasStartedRef.current = true;
    setStatus('loading');
    setEnabledCount(0);
    setTimedOut(false);
    setCheckboxChecked(false);
    setUnverifiedImages([]);
    pollingCancelledRef.current = false;

    const timeoutId = setTimeout(() => setTimedOut(true), TIMEOUT_MS);

    // Local flag so each effect run can only cancel its own loop.
    // pollingCancelledRef is kept for the continueAnyway callback.
    let cancelled = false;

    const pollImages = async () => {
      const POLL_INTERVAL_MS = 2000;

      while (!cancelled && !pollingCancelledRef.current) {
        try {
          const pollStartTime = Date.now();

          refetchImages();

          const currentImages = imagesRef.current;
          const enabled = currentImages.filter((img) => img.enabled && img.aligned === true).length;
          setEnabledCount(enabled);

          if (currentImages.length >= uploadedImageCount && enabled === uploadedImageCount) {
            clearTimeout(timeoutId);
            setStatus('success');
            return;
          }

          const elapsedTime = Date.now() - pollStartTime;
          const remainingTime = Math.max(0, POLL_INTERVAL_MS - elapsedTime);
          if (remainingTime > 0) {
            await new Promise((resolve) => setTimeout(resolve, remainingTime));
          }
        } catch (error) {
          console.warn('Poll failed, continuing:', error);
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        }
      }
    };

    void pollImages();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      // Reset so a remount (e.g. React Strict Mode double-invocation) can start fresh.
      hasStartedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geodataId, imageStepStatus, uploadedImageCount, refetchImages]);

  return {
    status,
    enabledCount,
    totalCount: images.length,
    uploadStepStatus,
    errorList,
  };
}

type ControlImagesErrorBoxProps = {
  timedOut: boolean;
  status: ControlStatus;
  failedImages: string[];
  unverifiedImages: string[];
  enabledCount: number;
  checkboxChecked: boolean;
  onCheckboxChange: (checked: boolean) => void;
  onContinueAnyway: () => void;
};

// exported for unit testing only
export function ControlImagesErrorBox({
  timedOut,
  status,
  failedImages,
  unverifiedImages,
  enabledCount,
  checkboxChecked,
  onCheckboxChange,
  onContinueAnyway,
}: ControlImagesErrorBoxProps) {
  const { t } = useTranslation();

  if ((!timedOut || status === 'success') && failedImages.length === 0) {
    return null;
  }

  const allAccountedFor = unverifiedImages.length === 0;
  const showContinueAnywayButton = (timedOut || allAccountedFor) && enabledCount > 2;
  const boxColor: 'red' | 'yellow' = allAccountedFor && failedImages.length > 0 ? 'red' : 'yellow';
  // WS names carry a server prefix ("1716472378_filename.jpg"); strip it for display only.
  const stripPrefix = (n: string) => n.substring(n.indexOf('_') + 1);

  return (
    <div data-color={boxColor} data-testid="control-images-error-box">
      <InfoBox color={boxColor}>
        <ErrorContent>
          {timedOut && unverifiedImages.length > 0 && (
            <div>{t('geodataSetup.controlImages.timeoutMessage', { ns: 'cloudProcessing' })}</div>
          )}
          {showContinueAnywayButton && (
            <>
              <CheckBox
                checked={checkboxChecked}
                onChange={(e) => onCheckboxChange(e.target.checked)}
              >
                {t('geodataSetup.controlImages.timeoutCheckbox', { ns: 'cloudProcessing' })}
              </CheckBox>
              <Button
                color="primary"
                disabled={!checkboxChecked}
                variant="contained"
                width="fit-content"
                onClick={onContinueAnyway}
              >
                {t('geodataSetup.controlImages.continueAnyway', { ns: 'cloudProcessing' })}
              </Button>
            </>
          )}
          {failedImages.length > 0 && (
            <ImageSection
              images={failedImages.map(stripPrefix)}
              title={t('geodataSetup.controlImages.failedImagesTitle', { ns: 'cloudProcessing' })}
            />
          )}
          {unverifiedImages.length > 0 && timedOut && (
            <ImageSection
              images={unverifiedImages}
              title={t('geodataSetup.controlImages.unverifiedImagesTitle', {
                ns: 'cloudProcessing',
              })}
            />
          )}
        </ErrorContent>
      </InfoBox>
    </div>
  );
}

function ImageSection({ title, images }: { title: string; images: string[] }) {
  const { t } = useTranslation();
  return (
    <div>
      <SectionTitle>{title}</SectionTitle>
      <ImagesList>
        {images.slice(0, 50).map((name, index) => (
          <ImageItem key={index}>{name}</ImageItem>
        ))}
        {images.length > 50 && (
          <ImageItem>
            {t('geodataSetup.controlImages.moreItems', {
              count: images.length - 50,
              ns: 'cloudProcessing',
            })}
          </ImageItem>
        )}
      </ImagesList>
    </div>
  );
}

const ErrorContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SectionTitle = styled.div`
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const ImagesList = styled.div`
  background-color: white;
  max-height: 200px;
  overflow-y: auto;
  padding-left: 1rem;
  border: 1px solid ${(props) => props.theme.color.gray.light};
`;

const ImageItem = styled.div`
  padding: 0.25rem 0;
  font-size: 0.9em;
  word-break: break-word;
`;
